import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { useEnumDetail, useUpsertEnum } from '../../../hooks/useDbDictionary.js';
import { useAuth } from '../../../hooks/useAuth.js';

interface EnumDetailPopupProps {
  /** 表示する Enum 名 (qualifiedName) */
  enumName: string;
  /** 親テーブル (キャッシュ無効化用) */
  parentTable: { schema: string; table: string };
  /** popup を閉じるコールバック */
  onClose: () => void;
}

interface EnumRow {
  key: string;
  value: string;
}

/**
 * "KEY=value, KEY2=value2" 風の文字列を行に分解。空白とカンマで分割
 * @graph-connects none
 */
function parseValues(values: string | null): EnumRow[] {
  if (!values) return [];
  return values
    .split(/\s*,\s*/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((entry) => {
      // "KEY=value" 形式。値側に "(..." が含まれていてもキー側は = 前まで
      const eq = entry.indexOf('=');
      if (eq < 0) return { key: entry, value: '' };
      return { key: entry.slice(0, eq).trim(), value: entry.slice(eq + 1).trim() };
    });
}

/**
 * 旧形式 Enum の fallback。enumValues フィールドが NULL で textForEmbedding に
 * "値: KEY=value, ..." の形で入っているものから値リストを復元する。
 * @graph-connects none
 */
function extractValuesFromTextForEmbedding(text: string | null): string | null {
  if (!text) return null;
  // 「値: ...」または「Values: ...」行を抽出 (改行 or 「定義:」 で終端)
  const match = /(?:値|Values?)\s*[:：]\s*([^\n]+?)(?:\n|定義\s*[:：]|$)/m.exec(text);
  return match?.[1]?.trim() ?? null;
}

/**
 * 行配列を "KEY=value, KEY2=value2" 形式に再構築
 * @graph-connects none
 */
function stringifyValues(rows: EnumRow[]): string {
  return rows
    .filter((r) => r.key.trim().length > 0)
    .map((r) => (r.value ? `${r.key.trim()}=${r.value.trim()}` : r.key.trim()))
    .join(', ');
}

/**
 * Enum 詳細ポップアップ
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Enum タグクリックで開くポップアップ。enum 値の追加/編集/削除 + 接続カラム (影響範囲) 表示 + 決定ボタンで PUT /enums/:name を呼ぶ
 * @graph-connects useEnumDetail [calls] enum 詳細取得
 * @graph-connects useUpsertEnum [calls] enum upsert mutation
 */
export function EnumDetailPopup({ enumName, parentTable, onClose }: EnumDetailPopupProps) {
  const { can } = useAuth();
  const canEdit = can('EDIT_DB_DICTIONARY');
  const { data, isLoading, isError } = useEnumDetail(enumName);
  const upsertEnum = useUpsertEnum(parentTable);

  const initialRows = useMemo(
    () =>
      parseValues(data?.enumValues ?? extractValuesFromTextForEmbedding(data?.description ?? null)),
    [data?.enumValues, data?.description],
  );
  const [rows, setRows] = useState<EnumRow[]>(initialRows);
  const [description, setDescription] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setRows(initialRows);
    setDescription(data?.description ?? '');
    setDirty(false);
  }, [initialRows, data?.description]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  const updateRow = (idx: number, patch: Partial<EnumRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const removeRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const addRow = () => {
    setRows((prev) => [...prev, { key: '', value: '' }]);
    setDirty(true);
  };

  const handleSave = () => {
    if (!data) return;
    const newValues = stringifyValues(rows);
    upsertEnum.mutate(
      {
        name: data.qualifiedName,
        description: description || (data.description ?? ''),
        values: newValues,
        repository: data.repository ?? 'air-closet-config',
      },
      {
        onSuccess: () => {
          setDirty(false);
        },
      },
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[calc(var(--z-modal)+1)] flex items-start justify-center pt-[4.5rem] pb-8"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-3xl mx-4 max-h-[calc(100vh-4rem)] overflow-y-auto bg-[var(--color-bg-surface)] rounded-xl border border-[var(--color-border)] shadow-2xl animate-fade-in"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* ヘッダー */}
        <div className="sticky top-0 z-10 bg-[var(--color-bg-surface)] border-b border-[var(--color-border)] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-[var(--color-text-primary)] font-mono">
              {enumName}
            </h2>
            {data?.repository && (
              <Badge variant="info" size="sm">
                {data.repository}
              </Badge>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5 text-[var(--color-text-muted)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          {isLoading && (
            <div className="space-y-2 animate-pulse">
              <div className="h-4 w-32 rounded bg-[var(--color-bg-elevated)]" />
              <div className="h-20 rounded bg-[var(--color-bg-elevated)]" />
              <div className="h-4 w-40 rounded bg-[var(--color-bg-elevated)]" />
              <div className="h-32 rounded bg-[var(--color-bg-elevated)]" />
            </div>
          )}

          {isError && (
            <div className="text-sm text-[var(--color-error)]">Enum の取得に失敗しました</div>
          )}

          {data && (
            <>
              {/* 説明 */}
              <section>
                <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                  説明 (textForEmbedding)
                </h3>
                {canEdit ? (
                  <textarea
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setDirty(true);
                    }}
                    rows={3}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)] resize-y"
                  />
                ) : (
                  <p className="text-sm text-[var(--color-text-primary)] whitespace-pre-wrap rounded-lg bg-[var(--color-bg-elevated)] p-3 border border-[var(--color-border)]">
                    {description || '-'}
                  </p>
                )}
              </section>

              {/* 値リスト */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-[var(--color-text-secondary)]">値</h3>
                  {canEdit && (
                    <Button variant="ghost" size="sm" onClick={addRow}>
                      + 行を追加
                    </Button>
                  )}
                </div>
                <div className="space-y-1">
                  {rows.length === 0 && (
                    <p className="text-sm text-[var(--color-text-muted)]">値が登録されていません</p>
                  )}
                  {rows.map((row, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2"
                    >
                      <input
                        type="text"
                        value={row.key}
                        onChange={(e) => {
                          updateRow(idx, { key: e.target.value });
                        }}
                        placeholder="KEY"
                        disabled={!canEdit}
                        className="flex-1 min-w-0 rounded border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 py-1 text-sm font-mono text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)] disabled:opacity-60"
                      />
                      <span className="text-[var(--color-text-muted)]">=</span>
                      <input
                        type="text"
                        value={row.value}
                        onChange={(e) => {
                          updateRow(idx, { value: e.target.value });
                        }}
                        placeholder="value"
                        disabled={!canEdit}
                        className="flex-1 min-w-0 rounded border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 py-1 text-sm font-mono text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)] disabled:opacity-60"
                      />
                      {canEdit && (
                        <button
                          onClick={() => {
                            removeRow(idx);
                          }}
                          className="p-1.5 rounded hover:bg-[var(--color-error)]/10 text-[var(--color-text-muted)] hover:text-[var(--color-error)] transition-colors"
                          aria-label="行を削除"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* 影響範囲: 接続カラム */}
              <section>
                <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                  影響範囲: この Enum を参照しているカラム ({data.connectedColumns.length})
                </h3>
                {data.connectedColumns.length === 0 ? (
                  <p className="text-sm text-[var(--color-text-muted)]">接続カラムなし</p>
                ) : (
                  <ul className="space-y-1">
                    {data.connectedColumns.map((c) => (
                      <li
                        key={c.columnQN}
                        className="text-sm font-mono rounded bg-[var(--color-bg-elevated)] px-3 py-1.5 border border-[var(--color-border)]"
                      >
                        <span className="text-[var(--color-text-primary)]">{c.columnQN}</span>
                        {c.tableDescription && (
                          <span className="ml-2 text-xs text-[var(--color-text-muted)] font-sans">
                            — {c.tableDescription.slice(0, 80)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* 決定ボタン */}
              {canEdit && (
                <div className="flex justify-end gap-2 sticky bottom-0 bg-[var(--color-bg-surface)] pt-2 -mx-6 px-6 border-t border-[var(--color-border)]">
                  <Button variant="ghost" size="sm" onClick={onClose}>
                    閉じる
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    loading={upsertEnum.isPending}
                    disabled={!dirty}
                    onClick={handleSave}
                  >
                    決定 (PUT)
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
